<template>
  <div class="scanner-page py-3">
    <header class="scanner-toolbar">
      <div class="scanner-toolbar__title">
        <span class="scanner-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>{{ mode === 'ship' ? 'Ready To Ship Scanner' : 'Return Scanner' }}</h1>
          <p>{{ mode === 'ship' ? 'Scan order & tracking barcodes to prepare shipments' : 'Scan an order to mark it as returned' }}</p>
        </div>
      </div>
    </header>

    <div class="mode-tabs" role="tablist">
      <button
        type="button"
        role="tab"
        :class="['mode-tab', { 'is-active': mode === 'ship' }]"
        :aria-selected="mode === 'ship'"
        @click="setMode('ship')"
      >
        <span>Ready to Ship</span>
        <span v-if="scannedRows.length" class="mode-tab__badge">{{ scannedRows.length }}</span>
      </button>
      <button
        type="button"
        role="tab"
        :class="['mode-tab', { 'is-active': mode === 'return' }]"
        :aria-selected="mode === 'return'"
        @click="setMode('return')"
      >
        <span>Return Scanner</span>
        <span v-if="returnedRows.length" class="mode-tab__badge">{{ returnedRows.length }}</span>
      </button>
    </div>

    <CAlert v-if="toast.message" :color="toast.kind" show class="scanner-alert">
      {{ toast.message }}
    </CAlert>

    <section v-if="mode === 'ship'" class="scan-zone">
      <div class="scan-zone__head">
        <h2>Scan zone</h2>
        <span class="scan-zone__hint">Use your barcode scanner — values auto-advance on Enter</span>
      </div>
      <div class="scan-zone__grid">
        <div :class="['scan-field', { 'is-active': activeField === 'order', 'is-locked': activeField !== 'order' }]">
          <div class="scan-field__step">Step 1</div>
          <label for="scan-order" class="scan-field__label">Order Number</label>
          <input
            id="scan-order"
            ref="orderInput"
            v-model="orderInputValue"
            type="text"
            class="scan-input"
            autocomplete="off"
            spellcheck="false"
            inputmode="text"
            placeholder="Scan or type order number"
            :disabled="isSubmitting"
            @keydown.enter.prevent="onOrderEnter"
            @focus="activeField = 'order'"
          >
          <div v-if="pendingOrderNumber" class="scan-field__pending">
            <span class="scan-field__pending-dot" aria-hidden="true"></span>
            Captured: <strong>{{ pendingOrderNumber }}</strong>
            <span v-if="isLookingUp" class="scan-field__lookup-hint">Looking up…</span>
            <template v-else>
              <span v-if="pendingBuyerName" class="scan-field__pending-buyer">{{ pendingBuyerName }}</span>
              <span v-if="pendingShippingMethod" class="ship-tag ship-tag--inline">{{ formatShippingMethod(pendingShippingMethod) }}</span>
            </template>
            <button type="button" class="scan-field__cancel" @click="cancelPending">Cancel</button>
          </div>
        </div>

        <div :class="['scan-field', { 'is-active': activeField === 'tracking', 'is-locked': activeField !== 'tracking' }]">
          <div class="scan-field__step">Step 2</div>
          <label for="scan-tracking" class="scan-field__label">Tracking ID</label>
          <input
            id="scan-tracking"
            ref="trackingInput"
            v-model="trackingInputValue"
            type="text"
            class="scan-input"
            autocomplete="off"
            spellcheck="false"
            inputmode="text"
            placeholder="Scan or type tracking ID"
            :disabled="isSubmitting || !pendingOrderNumber"
            @keydown.enter.prevent="onTrackingEnter"
            @focus="activeField = 'tracking'"
          >
          <div v-if="!pendingOrderNumber" class="scan-field__hint-text">Capture an order number first.</div>
        </div>
      </div>

      <div v-if="lineError" class="scan-zone__error" role="alert">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        {{ lineError }}
      </div>
    </section>

    <div v-if="mode === 'ship'" class="status-bar">
      <div class="status-bar__group">
        <span class="status-pill">
          <span class="status-pill__dot" aria-hidden="true"></span>
          Pairs ready: <strong>{{ scannedRows.length }}</strong>
        </span>
        <span v-if="failedRowCount" class="status-pill status-pill--danger">
          Failed: <strong>{{ failedRowCount }}</strong>
        </span>
      </div>
      <div class="scanner-toolbar__actions">
        <button
          v-if="scannedRows.length"
          type="button"
          class="ghost-btn"
          :disabled="isSubmitting"
          @click="confirmClearAll"
        >Clear all</button>
        <button
          type="button"
          class="submit-btn"
          :disabled="!canSubmit"
          @click="confirmSubmit"
        >
          <svg v-if="!isSubmitting" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          <span v-if="isSubmitting" class="submit-btn__spinner" aria-hidden="true"></span>
          <span>{{ isSubmitting ? 'Submitting…' : `Submit ${scannedRows.length || ''} pair${scannedRows.length === 1 ? '' : 's'}` }}</span>
        </button>
      </div>
    </div>

    <section v-if="mode === 'ship'" class="rows-card">
      <div class="rows-card__head">
        <h2>Scanned pairs</h2>
        <span v-if="scannedRows.length" class="rows-card__count">{{ scannedRows.length }}</span>
      </div>

      <div v-if="!scannedRows.length" class="rows-card__empty">
        <div class="empty-glyph" aria-hidden="true">⌫</div>
        <p>No pairs scanned yet. Start by scanning an order number above.</p>
      </div>

      <div v-else class="rows-card__scroll">
        <table class="rows-table">
          <thead>
            <tr>
              <th class="col-num">#</th>
              <th>Order Number</th>
              <th>Tracking ID</th>
              <th>Shipping Method</th>
              <th>Scanned At</th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in scannedRows"
              :key="row.uid"
              :class="{ 'is-failed': row.error, 'is-editing': editingUid === row.uid }"
            >
              <td class="col-num">{{ index + 1 }}</td>
              <td class="cell-order">
                <template v-if="editingUid === row.uid">
                  <input
                    v-model="editDraft.order_number"
                    type="text"
                    class="row-edit-input"
                    autocomplete="off"
                    spellcheck="false"
                    @keydown.enter.prevent="saveEdit(row.uid)"
                    @keydown.esc.prevent="cancelEdit"
                  >
                  <span v-if="editError" class="row-edit-error">{{ editError }}</span>
                </template>
                <template v-else>
                  <span class="cell-mono">{{ row.order_number }}</span>
                  <span v-if="row.buyer_name" class="cell-buyer">{{ row.buyer_name }}</span>
                  <span v-if="row.error" class="row-error">{{ row.error }}</span>
                </template>
              </td>
              <td>
                <input
                  v-if="editingUid === row.uid"
                  v-model="editDraft.tracking_id"
                  type="text"
                  class="row-edit-input"
                  autocomplete="off"
                  spellcheck="false"
                  @keydown.enter.prevent="saveEdit(row.uid)"
                  @keydown.esc.prevent="cancelEdit"
                >
                <span v-else class="cell-mono">{{ row.tracking_id }}</span>
              </td>
              <td>
                <span v-if="row.shipping_method" class="ship-tag">{{ formatShippingMethod(row.shipping_method) }}</span>
                <span v-else class="cell-muted">—</span>
              </td>
              <td><span class="cell-time">{{ formatTime(row.scanned_at) }}</span></td>
              <td class="text-right">
                <div class="row-actions">
                  <template v-if="editingUid === row.uid">
                    <button
                      type="button"
                      class="row-save-btn"
                      :disabled="isSubmitting"
                      @click="saveEdit(row.uid)"
                    >Save</button>
                    <button
                      type="button"
                      class="row-ghost-btn"
                      :disabled="isSubmitting"
                      @click="cancelEdit"
                    >Cancel</button>
                  </template>
                  <template v-else>
                    <button
                      type="button"
                      class="row-edit-btn"
                      :disabled="isSubmitting || editingUid !== null"
                      @click="startEdit(row)"
                    >Edit</button>
                    <button
                      type="button"
                      class="row-remove-btn"
                      :disabled="isSubmitting || editingUid !== null"
                      @click="removeRow(row.uid)"
                    >Remove</button>
                  </template>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section v-if="mode === 'return'" class="scan-zone">
      <div class="scan-zone__head">
        <h2>Scan zone</h2>
        <span class="scan-zone__hint">Scan an order number to load a return preview</span>
      </div>
      <div class="return-scan">
        <div :class="['scan-field', 'is-active']">
          <div class="scan-field__step">Order Number</div>
          <input
            id="scan-return-order"
            ref="returnOrderInput"
            v-model="returnOrderInputValue"
            type="text"
            class="scan-input"
            autocomplete="off"
            spellcheck="false"
            inputmode="text"
            placeholder="Scan or type order number"
            :disabled="returnIsConfirming"
            @keydown.enter.prevent="onReturnOrderEnter"
          >
          <div v-if="returnIsLookingUp" class="scan-field__hint-text">Looking up order…</div>
          <div v-else-if="returnLookupError" class="scan-zone__error" role="alert" style="margin-top: 0.5rem;">
            {{ returnLookupError }}
          </div>
        </div>
      </div>

      <div v-if="returnPreview" class="return-preview">
        <div class="return-preview__head">
          <span class="return-preview__label">Confirm return</span>
          <button type="button" class="scan-field__cancel" @click="cancelReturnPreview" :disabled="returnIsConfirming">Cancel</button>
        </div>
        <div class="return-preview__order">
          <span class="return-preview__order-num">{{ returnPreview.order_number }}</span>
          <span v-if="returnPreview.buyer_name" class="return-preview__buyer">{{ returnPreview.buyer_name }}</span>
        </div>
        <div class="return-preview__grid">
          <div class="return-preview__field">
            <span class="return-preview__field-label">Order Status</span>
            <span :class="['status-pill', statusPillClass(returnPreview.order_status)]">
              {{ returnPreview.order_status || 'unknown' }}
            </span>
          </div>
          <div class="return-preview__field">
            <span class="return-preview__field-label">Shipping Status</span>
            <span :class="['status-pill', statusPillClass(returnPreview.shipping_status)]">
              {{ returnPreview.shipping_status || 'unknown' }}
            </span>
          </div>
          <div v-if="returnPreview.shipping_method" class="return-preview__field">
            <span class="return-preview__field-label">Shipping Method</span>
            <span class="ship-tag">{{ formatShippingMethod(returnPreview.shipping_method) }}</span>
          </div>
          <div v-if="returnPreview.tracking_id" class="return-preview__field">
            <span class="return-preview__field-label">Tracking ID</span>
            <span class="cell-mono">{{ returnPreview.tracking_id }}</span>
          </div>
          <div v-if="returnPreview.contact_phone" class="return-preview__field">
            <span class="return-preview__field-label">Phone</span>
            <span class="cell-mono">{{ returnPreview.contact_phone }}</span>
          </div>
          <div v-if="returnPreview.grand_total != null" class="return-preview__field">
            <span class="return-preview__field-label">Total</span>
            <span class="cell-mono">{{ returnPreview.grand_total }}</span>
          </div>
        </div>
        <div v-if="returnPreview.order_status === 'returned' || returnPreview.shipping_status === 'returned'" class="return-preview__warning">
          This order is already marked as returned.
        </div>
        <div class="return-preview__actions">
          <button
            type="button"
            class="ghost-btn"
            :disabled="returnIsConfirming"
            @click="cancelReturnPreview"
          >Cancel</button>
          <button
            type="button"
            class="danger-btn"
            :disabled="returnIsConfirming"
            @click="confirmReturn"
          >
            <span v-if="returnIsConfirming" class="submit-btn__spinner" aria-hidden="true"></span>
            <span>{{ returnIsConfirming ? 'Marking…' : 'Confirm Return' }}</span>
          </button>
        </div>
      </div>
    </section>

    <section v-if="mode === 'return'" class="rows-card">
      <div class="rows-card__head">
        <h2>Marked as returned</h2>
        <span v-if="returnedRows.length" class="rows-card__count">{{ returnedRows.length }}</span>
      </div>

      <div v-if="!returnedRows.length" class="rows-card__empty">
        <div class="empty-glyph" aria-hidden="true">↩</div>
        <p>No orders marked as returned yet. Scan an order number above.</p>
      </div>

      <div v-else class="rows-card__scroll">
        <table class="rows-table">
          <thead>
            <tr>
              <th class="col-num">#</th>
              <th>Order Number</th>
              <th>Marked At</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in returnedRows" :key="row.uid">
              <td class="col-num">{{ index + 1 }}</td>
              <td class="cell-order">
                <span class="cell-mono">{{ row.order_number }}</span>
                <span v-if="row.buyer_name" class="cell-buyer">{{ row.buyer_name }}</span>
              </td>
              <td><span class="cell-time">{{ formatTime(row.marked_at) }}</span></td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <div v-if="showSubmitConfirm" class="confirm-overlay" role="dialog" aria-modal="true" @click.self="showSubmitConfirm = false">
      <div class="confirm-dialog">
        <h3>Submit scanned pairs?</h3>
        <p>This will submit {{ scannedRows.length }} scanned pair{{ scannedRows.length === 1 ? '' : 's' }} to be marked ready to ship.</p>
        <div class="confirm-dialog__actions">
          <button type="button" class="ghost-btn" @click="showSubmitConfirm = false">Cancel</button>
          <button type="button" class="submit-btn" @click="proceedSubmit">Yes, submit</button>
        </div>
      </div>
    </div>

    <div v-if="showClearConfirm" class="confirm-overlay" role="dialog" aria-modal="true" @click.self="showClearConfirm = false">
      <div class="confirm-dialog">
        <h3>Clear all scanned pairs?</h3>
        <p>This will discard {{ scannedRows.length }} unsaved pair{{ scannedRows.length === 1 ? '' : 's' }}.</p>
        <div class="confirm-dialog__actions">
          <button type="button" class="ghost-btn" @click="showClearConfirm = false">Keep them</button>
          <button type="button" class="danger-btn" @click="clearAll">Yes, clear</button>
        </div>
      </div>
    </div>

    <div v-if="showSubmitResult" class="confirm-overlay" role="dialog" aria-modal="true" @click.self="closeSubmitResult">
      <div class="result-dialog">
        <div :class="['result-dialog__icon', submitResult.ok ? 'is-success' : 'is-error']">
          <svg v-if="submitResult.ok" viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          <svg v-else viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        </div>

        <h3 class="result-dialog__title">
          <template v-if="submitResult.ok && !submitResult.failed.length">Successfully scanned! 🎉</template>
          <template v-else-if="submitResult.ok && submitResult.failed.length">Partially saved</template>
          <template v-else>Submission failed</template>
        </h3>

        <p class="result-dialog__subtitle">
          <template v-if="submitResult.ok">
            <strong>{{ submitResult.ok }}</strong> order{{ submitResult.ok === 1 ? '' : 's' }} scanned &amp; marked ready to ship.
          </template>
          <template v-else>None of the scanned orders could be saved.</template>
        </p>

        <div v-if="submitResult.failed.length" class="result-fail">
          <div class="result-fail__head">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            {{ submitResult.failed.length }} failed — left in the table for review
          </div>
          <ul class="result-fail__list">
            <li v-for="(f, i) in submitResult.failed" :key="i" class="result-fail__item">
              <span class="result-fail__order cell-mono">{{ f.order_number }}</span>
              <span class="result-fail__reason">{{ f.reason }}</span>
            </li>
          </ul>
        </div>

        <div class="confirm-dialog__actions">
          <button type="button" class="submit-btn" @click="closeSubmitResult">Done</button>
        </div>
      </div>
    </div>

    <canvas ref="confettiCanvas" class="confetti-canvas" aria-hidden="true"></canvas>
  </div>
</template>

<script>
import axios from 'axios'

const STORAGE_KEY = 'scannerReadyToShip:v1'

function generateUid () {
  return `r-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function trimScanValue (value) {
  if (value === null || value === undefined) return ''
  return String(value).replace(/[\r\n\t]/g, '').trim()
}

function extractOrderNumber (value) {
  return trimScanValue(value).split('|')[0].trim()
}

export default {
  name: 'ScannerReadyToShip',
  data () {
    return {
      mode: 'ship',
      orderInputValue: '',
      trackingInputValue: '',
      pendingOrderNumber: '',
      pendingShippingMethod: '',
      pendingBuyerName: '',
      isLookingUp: false,
      lookupSeq: 0,
      activeField: 'order',
      scannedRows: [],
      lineError: '',
      isSubmitting: false,
      toast: { message: '', kind: 'success' },
      toastTimer: null,
      showClearConfirm: false,
      showSubmitConfirm: false,
      showSubmitResult: false,
      submitResult: { ok: 0, failed: [] },
      confettiRaf: null,
      editingUid: null,
      editDraft: { order_number: '', tracking_id: '' },
      editError: '',
      returnOrderInputValue: '',
      returnPreview: null,
      returnIsLookingUp: false,
      returnIsConfirming: false,
      returnLookupSeq: 0,
      returnLookupError: '',
      returnedRows: []
    }
  },
  computed: {
    canSubmit () {
      return !this.isSubmitting && this.scannedRows.length > 0
    },
    failedRowCount () {
      return this.scannedRows.filter((row) => Boolean(row.error)).length
    }
  },
  watch: {
    scannedRows: {
      deep: true,
      handler () {
        this.persistState()
      }
    },
    pendingOrderNumber () {
      this.persistState()
    },
    returnedRows: {
      deep: true,
      handler () {
        this.persistState()
      }
    },
    mode () {
      this.persistState()
    }
  },
  created () {
    this.restoreState()
  },
  mounted () {
    this.$nextTick(() => this.focusActiveModeInput())
  },
  beforeDestroy () {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer)
    }
    if (this.confettiRaf) {
      cancelAnimationFrame(this.confettiRaf)
    }
  },
  methods: {
    focusOrderInput () {
      const el = this.$refs.orderInput
      if (el && typeof el.focus === 'function') {
        el.focus()
        el.select && el.select()
      }
    },
    focusTrackingInput () {
      const el = this.$refs.trackingInput
      if (el && typeof el.focus === 'function') {
        el.focus()
        el.select && el.select()
      }
    },
    onOrderEnter () {
      const value = extractOrderNumber(this.orderInputValue)
      if (!value) {
        this.orderInputValue = ''
        this.focusOrderInput()
        return
      }

      const formatError = this.validateFormat(value, 'Order number')
      if (formatError) {
        this.flashError(formatError)
        this.orderInputValue = ''
        this.focusOrderInput()
        return
      }

      const duplicateInBatch = this.scannedRows.find((row) => row.order_number === value && !row.error)
      if (duplicateInBatch) {
        this.flashError('Order already scanned in this batch.')
        this.orderInputValue = ''
        this.focusOrderInput()
        return
      }

      this.lineError = ''
      this.pendingOrderNumber = value
      this.pendingShippingMethod = ''
      this.orderInputValue = ''
      this.activeField = 'tracking'
      this.$nextTick(() => this.focusTrackingInput())
      this.lookupShippingMethod(value)
    },
    onTrackingEnter () {
      const trackingValue = trimScanValue(this.trackingInputValue)

      if (!this.pendingOrderNumber) {
        this.flashError('Scan an order number first.')
        this.trackingInputValue = ''
        this.activeField = 'order'
        this.$nextTick(() => this.focusOrderInput())
        return
      }

      if (!trackingValue) {
        this.trackingInputValue = ''
        this.focusTrackingInput()
        return
      }

      const formatError = this.validateFormat(trackingValue, 'Tracking ID')
      if (formatError) {
        this.flashError(formatError)
        this.trackingInputValue = ''
        this.focusTrackingInput()
        return
      }

      const duplicateOrder = this.scannedRows.find((row) => row.order_number === this.pendingOrderNumber && !row.error)
      if (duplicateOrder) {
        this.flashError('Order already scanned in this batch.')
        this.resetPair()
        return
      }

      const duplicateTracking = this.scannedRows.find((row) => row.tracking_id === trackingValue && !row.error)
      if (duplicateTracking) {
        this.flashError('Tracking ID already used in this batch.')
        this.trackingInputValue = ''
        this.focusTrackingInput()
        return
      }

      this.scannedRows = [
        ...this.scannedRows,
        {
          uid: generateUid(),
          order_number: this.pendingOrderNumber,
          tracking_id: trackingValue,
          scanned_at: new Date().toISOString(),
          shipping_method: this.pendingShippingMethod || '',
          buyer_name: this.pendingBuyerName || '',
          error: ''
        }
      ]

      this.resetPair()
    },
    validateFormat (value, label) {
      if (value.length < 3) {
        return `${label} is too short.`
      }
      if (value.length > 64) {
        return `${label} is too long.`
      }
      if (!/^[\w\-./|:]+$/.test(value)) {
        return `${label} contains unsupported characters.`
      }
      return ''
    },
    resetPair () {
      this.pendingOrderNumber = ''
      this.pendingShippingMethod = ''
      this.pendingBuyerName = ''
      this.isLookingUp = false
      this.trackingInputValue = ''
      this.orderInputValue = ''
      this.activeField = 'order'
      this.lineError = ''
      this.$nextTick(() => this.focusOrderInput())
    },
    cancelPending () {
      this.pendingOrderNumber = ''
      this.pendingShippingMethod = ''
      this.pendingBuyerName = ''
      this.isLookingUp = false
      this.trackingInputValue = ''
      this.activeField = 'order'
      this.$nextTick(() => this.focusOrderInput())
    },
    async lookupShippingMethod (orderNumber) {
      const seq = ++this.lookupSeq
      this.isLookingUp = true
      try {
        const response = await axios.post(`${this.$apiAdress}/api/orders/scanner_lookup_order`, { order_number: orderNumber })
        if (seq !== this.lookupSeq || this.pendingOrderNumber !== orderNumber) return
        const data = response && response.data ? response.data : {}
        this.pendingShippingMethod = data.found && data.shipping_method ? String(data.shipping_method) : ''
        this.pendingBuyerName = data.found && data.buyer_name ? String(data.buyer_name) : ''
      } catch (e) {
        if (seq !== this.lookupSeq) return
        this.pendingShippingMethod = ''
        this.pendingBuyerName = ''
      } finally {
        if (seq === this.lookupSeq) this.isLookingUp = false
      }
    },
    async fetchOrderMeta (orderNumber) {
      try {
        const response = await axios.post(`${this.$apiAdress}/api/orders/scanner_lookup_order`, { order_number: orderNumber })
        const data = response && response.data ? response.data : {}
        if (!data.found) return { shipping_method: '', buyer_name: '' }
        return {
          shipping_method: data.shipping_method ? String(data.shipping_method) : '',
          buyer_name: data.buyer_name ? String(data.buyer_name) : ''
        }
      } catch (e) {
        return { shipping_method: '', buyer_name: '' }
      }
    },
    startEdit (row) {
      if (this.isSubmitting) return
      this.editingUid = row.uid
      this.editDraft = { order_number: row.order_number, tracking_id: row.tracking_id }
      this.editError = ''
    },
    cancelEdit () {
      this.editingUid = null
      this.editDraft = { order_number: '', tracking_id: '' }
      this.editError = ''
    },
    async saveEdit (uid) {
      const original = this.scannedRows.find((row) => row.uid === uid)
      if (!original) {
        this.cancelEdit()
        return
      }

      const orderValue = extractOrderNumber(this.editDraft.order_number)
      const trackingValue = trimScanValue(this.editDraft.tracking_id)

      const orderError = this.validateFormat(orderValue, 'Order number')
      if (orderError) { this.editError = orderError; return }
      const trackingError = this.validateFormat(trackingValue, 'Tracking ID')
      if (trackingError) { this.editError = trackingError; return }

      const dupOrder = this.scannedRows.find((row) => row.uid !== uid && row.order_number === orderValue && !row.error)
      if (dupOrder) { this.editError = 'Order already scanned in this batch.'; return }
      const dupTracking = this.scannedRows.find((row) => row.uid !== uid && row.tracking_id === trackingValue && !row.error)
      if (dupTracking) { this.editError = 'Tracking ID already used in this batch.'; return }

      const orderChanged = orderValue !== original.order_number
      let shipping_method = original.shipping_method
      let buyer_name = original.buyer_name
      if (orderChanged) {
        const meta = await this.fetchOrderMeta(orderValue)
        shipping_method = meta.shipping_method
        buyer_name = meta.buyer_name
      }

      this.scannedRows = this.scannedRows.map((row) => {
        if (row.uid !== uid) return row
        return {
          ...row,
          order_number: orderValue,
          tracking_id: trackingValue,
          shipping_method,
          buyer_name,
          error: ''
        }
      })

      this.cancelEdit()
    },
    setMode (next) {
      if (this.mode === next) return
      this.mode = next
      this.$nextTick(() => this.focusActiveModeInput())
    },
    focusActiveModeInput () {
      if (this.mode === 'ship') {
        this.focusOrderInput()
      } else {
        this.focusReturnOrderInput()
      }
    },
    focusReturnOrderInput () {
      const el = this.$refs.returnOrderInput
      if (el && typeof el.focus === 'function') {
        el.focus()
        el.select && el.select()
      }
    },
    statusPillClass (status) {
      if (!status) return 'status-pill--neutral'
      const s = String(status).toLowerCase()
      if (s === 'returned' || s === 'cancelled') return 'status-pill--danger'
      if (s === 'shipped' || s === 'delivered') return 'status-pill--success'
      if (s === 'ready_to_ship' || s === 'processing') return 'status-pill--warn'
      return 'status-pill--neutral'
    },
    async onReturnOrderEnter () {
      const value = extractOrderNumber(this.returnOrderInputValue)
      this.returnLookupError = ''
      if (!value) {
        this.returnOrderInputValue = ''
        this.focusReturnOrderInput()
        return
      }

      const formatError = this.validateFormat(value, 'Order number')
      if (formatError) {
        this.returnLookupError = formatError
        this.returnOrderInputValue = ''
        this.focusReturnOrderInput()
        return
      }

      const seq = ++this.returnLookupSeq
      this.returnIsLookingUp = true
      this.returnPreview = null

      try {
        const response = await axios.post(`${this.$apiAdress}/api/orders/scanner_lookup_order`, { order_number: value })
        if (seq !== this.returnLookupSeq) return
        const data = response && response.data ? response.data : {}
        if (!data.found) {
          this.returnLookupError = `Order "${value}" not found.`
          return
        }
        this.returnPreview = {
          order_number: String(data.order_number || value),
          buyer_name: data.buyer_name ? String(data.buyer_name) : '',
          contact_phone: data.contact_phone ? String(data.contact_phone) : '',
          shipping_method: data.shipping_method ? String(data.shipping_method) : '',
          shipping_status: data.shipping_status ? String(data.shipping_status) : '',
          order_status: data.order_status ? String(data.order_status) : '',
          tracking_id: data.tracking_id ? String(data.tracking_id) : '',
          grand_total: data.grand_total != null ? data.grand_total : null
        }
        this.returnOrderInputValue = ''
      } catch (e) {
        if (seq !== this.returnLookupSeq) return
        const msg = (e && e.response && e.response.data && e.response.data.message) || 'Lookup failed.'
        this.returnLookupError = msg
      } finally {
        if (seq === this.returnLookupSeq) this.returnIsLookingUp = false
      }
    },
    cancelReturnPreview () {
      this.returnPreview = null
      this.returnOrderInputValue = ''
      this.returnLookupError = ''
      this.$nextTick(() => this.focusReturnOrderInput())
    },
    async confirmReturn () {
      if (!this.returnPreview || this.returnIsConfirming) return
      const orderNumber = this.returnPreview.order_number
      this.returnIsConfirming = true
      try {
        const response = await axios.post(`${this.$apiAdress}/api/orders/scanner_mark_returned`, { order_number: orderNumber })
        const data = response && response.data ? response.data : {}
        if (!data.success) {
          this.showToast('danger', data.message || 'Failed to mark returned.')
          return
        }
        this.returnedRows = [
          ...this.returnedRows,
          {
            uid: generateUid(),
            order_number: orderNumber,
            buyer_name: this.returnPreview.buyer_name,
            marked_at: new Date().toISOString()
          }
        ]
        this.showToast('success', `Order ${orderNumber} marked as returned.`)
        this.returnPreview = null
        this.returnOrderInputValue = ''
        this.$nextTick(() => this.focusReturnOrderInput())
      } catch (error) {
        const message = (error && error.response && error.response.data && error.response.data.message) ||
          'Network error, please retry.'
        this.showToast('danger', message)
      } finally {
        this.returnIsConfirming = false
      }
    },
    flashError (message) {
      this.lineError = message
      if (this.toastTimer) clearTimeout(this.toastTimer)
      this.toastTimer = setTimeout(() => {
        this.lineError = ''
      }, 3500)
    },
    removeRow (uid) {
      this.scannedRows = this.scannedRows.filter((row) => row.uid !== uid)
    },
    confirmClearAll () {
      if (!this.scannedRows.length) return
      this.showClearConfirm = true
    },
    clearAll () {
      this.scannedRows = []
      this.showClearConfirm = false
      this.cancelPending()
    },
    formatShippingMethod (slug) {
      if (!slug) return ''
      return String(slug)
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
    },
    formatTime (iso) {
      if (!iso) return '—'
      const date = new Date(iso)
      if (Number.isNaN(date.getTime())) return '—'
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    },
    showToast (kind, message, ttl = 4500) {
      this.toast = { kind, message }
      if (this.toastTimer) clearTimeout(this.toastTimer)
      this.toastTimer = setTimeout(() => {
        this.toast = { message: '', kind: 'success' }
      }, ttl)
    },
    confirmSubmit () {
      if (!this.canSubmit) return
      this.showSubmitConfirm = true
    },
    proceedSubmit () {
      this.showSubmitConfirm = false
      this.submitBatch()
    },
    closeSubmitResult () {
      this.showSubmitResult = false
      if (this.confettiRaf) {
        cancelAnimationFrame(this.confettiRaf)
        this.confettiRaf = null
      }
      this.$nextTick(() => {
        if (this.mode === 'ship' && this.activeField === 'order') {
          this.focusOrderInput()
        }
      })
    },
    launchConfetti () {
      const canvas = this.$refs.confettiCanvas
      if (!canvas || typeof canvas.getContext !== 'function') return
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      const W = canvas.width = window.innerWidth
      const H = canvas.height = window.innerHeight
      const colors = ['#0e5b46', '#16a07a', '#f59e0b', '#3b82f6', '#ef4444', '#a855f7']
      const gravity = 0.32
      const duration = 2600
      const start = Date.now()
      const particles = []

      for (let i = 0; i < 150; i++) {
        particles.push({
          x: W / 2 + (Math.random() - 0.5) * 220,
          y: H * 0.35,
          vx: (Math.random() - 0.5) * 13,
          vy: Math.random() * -15 - 4,
          size: Math.random() * 7 + 4,
          color: colors[Math.floor(Math.random() * colors.length)],
          rot: Math.random() * Math.PI,
          vrot: (Math.random() - 0.5) * 0.35,
          shape: Math.random() > 0.5 ? 'rect' : 'circle'
        })
      }

      if (this.confettiRaf) cancelAnimationFrame(this.confettiRaf)

      const animate = () => {
        const elapsed = Date.now() - start
        ctx.clearRect(0, 0, W, H)
        const alpha = Math.max(0, 1 - elapsed / duration)
        particles.forEach((p) => {
          p.vy += gravity
          p.x += p.vx
          p.y += p.vy
          p.rot += p.vrot
          ctx.save()
          ctx.globalAlpha = alpha
          ctx.translate(p.x, p.y)
          ctx.rotate(p.rot)
          ctx.fillStyle = p.color
          if (p.shape === 'rect') {
            ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6)
          } else {
            ctx.beginPath()
            ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2)
            ctx.fill()
          }
          ctx.restore()
        })
        if (elapsed < duration) {
          this.confettiRaf = requestAnimationFrame(animate)
        } else {
          ctx.clearRect(0, 0, W, H)
          this.confettiRaf = null
        }
      }
      this.confettiRaf = requestAnimationFrame(animate)
    },
    async submitBatch () {
      if (!this.canSubmit) return
      this.isSubmitting = true
      this.toast = { message: '', kind: 'success' }

      const payload = {
        shipments: this.scannedRows.map((row) => ({
          order_number: row.order_number,
          tracking_id: row.tracking_id
        }))
      }

      try {
        const response = await axios.post(`${this.$apiAdress}/api/orders/scanner_ready_to_ship`, payload)
        const data = response && response.data ? response.data : {}
        const processed = Array.isArray(data.processed) ? data.processed : []

        if (!processed.length) {
          this.showToast('warning', data.message || 'No rows were processed.')
          return
        }

        const statusByOrder = new Map()
        processed.forEach((entry) => {
          if (entry && entry.order_number) {
            statusByOrder.set(entry.order_number, entry)
          }
        })

        const remaining = []
        let okCount = 0
        let failCount = 0

        this.scannedRows.forEach((row) => {
          const entry = statusByOrder.get(row.order_number)
          if (entry && entry.status === 'ok') {
            okCount += 1
            return
          }
          if (entry && entry.status === 'failed') {
            failCount += 1
            remaining.push({
              ...row,
              error: entry.reason || 'Failed to save.',
              shipping_method: entry.shipping_method || row.shipping_method || ''
            })
            return
          }
          remaining.push({ ...row, error: row.error })
        })

        this.scannedRows = remaining

        this.submitResult = {
          ok: okCount,
          failed: remaining
            .filter((row) => row.error)
            .map((row) => ({
              order_number: row.order_number,
              tracking_id: row.tracking_id,
              reason: row.error
            }))
        }
        this.showSubmitResult = true

        if (okCount > 0 && failCount === 0) {
          this.cancelPending()
        }
        if (okCount > 0) {
          this.$nextTick(() => this.launchConfetti())
        }
      } catch (error) {
        const message = (error && error.response && error.response.data && error.response.data.message) ||
          'Network error, please retry.'
        this.showToast('danger', message)
      } finally {
        this.isSubmitting = false
        this.$nextTick(() => {
          if (this.activeField === 'order') {
            this.focusOrderInput()
          }
        })
      }
    },
    persistState () {
      try {
        const snapshot = {
          mode: this.mode,
          rows: this.scannedRows,
          pending: this.pendingOrderNumber,
          returnedRows: this.returnedRows
        }
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot))
      } catch (e) {
        // localStorage unavailable; ignore.
      }
    },
    restoreState () {
      try {
        const raw = window.localStorage.getItem(STORAGE_KEY)
        if (!raw) return
        const parsed = JSON.parse(raw)
        if (parsed && (parsed.mode === 'ship' || parsed.mode === 'return')) {
          this.mode = parsed.mode
        }
        if (parsed && Array.isArray(parsed.rows)) {
          this.scannedRows = parsed.rows
            .filter((row) => row && row.order_number && row.tracking_id)
            .map((row) => ({
              uid: row.uid || generateUid(),
              order_number: String(row.order_number),
              tracking_id: String(row.tracking_id),
              scanned_at: row.scanned_at || new Date().toISOString(),
              shipping_method: row.shipping_method || '',
              buyer_name: row.buyer_name || '',
              error: row.error || ''
            }))
        }
        if (parsed && Array.isArray(parsed.returnedRows)) {
          this.returnedRows = parsed.returnedRows
            .filter((row) => row && row.order_number)
            .map((row) => ({
              uid: row.uid || generateUid(),
              order_number: String(row.order_number),
              buyer_name: row.buyer_name || '',
              marked_at: row.marked_at || new Date().toISOString()
            }))
        }
        if (parsed && parsed.pending) {
          this.pendingOrderNumber = String(parsed.pending)
          this.activeField = this.pendingOrderNumber ? 'tracking' : 'order'
          if (this.pendingOrderNumber) {
            this.lookupShippingMethod(this.pendingOrderNumber)
          }
        }
      } catch (e) {
        // Corrupt storage; ignore.
      }
    }
  }
}
</script>

<style scoped>
.scanner-page {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

/* Toolbar */
.scanner-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 0.5rem;
}

.scanner-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.scanner-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.scanner-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.scanner-toolbar__title p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.scanner-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.submit-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 38px;
  padding: 0 1rem;
  border-radius: 8px;
  border: 0;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  filter: brightness(1.05);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
}

.submit-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}

.submit-btn__spinner {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff;
  animation: spin 0.65s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.ghost-btn {
  height: 38px;
  padding: 0 0.9rem;
  border: 1px solid #e5e9f2;
  border-radius: 8px;
  background: #ffffff;
  color: #1f2a44;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}

.ghost-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.ghost-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.danger-btn {
  height: 38px;
  padding: 0 0.9rem;
  border: 1px solid #dc2626;
  border-radius: 8px;
  background: #dc2626;
  color: #ffffff;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, box-shadow 0.15s ease;
}

.danger-btn:hover {
  background: #b91c1c;
  box-shadow: 0 2px 6px rgba(220, 38, 38, 0.25);
}

.scanner-alert {
  margin: 0;
  border-radius: 10px;
}

/* Mode tabs */
.mode-tabs {
  display: inline-flex;
  align-self: flex-start;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  padding: 4px;
  gap: 2px;
}

.mode-tab {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 34px;
  padding: 0 1rem;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #475569;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.mode-tab:hover:not(.is-active) {
  color: #1f2a44;
}

.mode-tab.is-active {
  background: #ffffff;
  color: #0e5b46;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
}

.mode-tab__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 0.4rem;
  background: #16a07a;
  color: #ffffff;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
}

.mode-tab:not(.is-active) .mode-tab__badge {
  background: #cbd5e1;
  color: #475569;
}

/* Return mode */
.return-scan {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  max-width: 480px;
}

.return-preview {
  margin-top: 1rem;
  border: 1px solid #fecdd3;
  background: #fff7f7;
  border-radius: 10px;
  padding: 0.9rem 1rem 1rem;
}

.return-preview__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.6rem;
}

.return-preview__label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #b91c1c;
}

.return-preview__order {
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin-bottom: 0.85rem;
}

.return-preview__order-num {
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2a44;
}

.return-preview__buyer {
  font-size: 0.85rem;
  font-weight: 500;
  color: #475569;
}

.return-preview__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.7rem 1rem;
  margin-bottom: 0.85rem;
}

.return-preview__field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.return-preview__field-label {
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
}

.return-preview__warning {
  margin: 0 0 0.85rem;
  padding: 0.45rem 0.7rem;
  background: #fff1f2;
  border: 1px solid #fecdd3;
  border-radius: 8px;
  color: #b91c1c;
  font-size: 0.78rem;
  font-weight: 600;
}

.return-preview__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.status-pill--success {
  border-color: #c8ead9;
  background: #f5fbf8;
  color: #0e5b46;
}

.status-pill--warn {
  border-color: #fde68a;
  background: #fffbeb;
  color: #92400e;
}

.status-pill--neutral {
  border-color: #e5e9f2;
  background: #f8fafc;
  color: #475569;
}

/* Scan zone */
.scan-zone {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  padding: 1rem 1.1rem 1.1rem;
}

.scan-zone__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.85rem;
}

.scan-zone__head h2 {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  margin: 0;
  letter-spacing: -0.01em;
}

.scan-zone__hint {
  font-size: 0.75rem;
  color: #6b7280;
}

.scan-zone__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem;
}

.scan-field {
  position: relative;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.75rem 0.85rem 0.85rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

.scan-field.is-active {
  background: #f5fbf8;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.scan-field.is-locked {
  opacity: 0.85;
}

.scan-field__step {
  display: inline-block;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #16a07a;
  margin-bottom: 0.25rem;
}

.scan-field.is-locked .scan-field__step {
  color: #94a3b8;
}

.scan-field__label {
  display: block;
  font-size: 0.82rem;
  font-weight: 700;
  color: #1f2a44;
  margin-bottom: 0.45rem;
}

.scan-input {
  width: 100%;
  height: 44px;
  padding: 0 0.85rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 1rem;
  font-weight: 600;
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  color: #1f2a44;
  letter-spacing: 0.01em;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.scan-input::placeholder {
  color: #9aa3b2;
  font-weight: 500;
  letter-spacing: 0.01em;
  font-family: inherit;
}

.scan-input:focus {
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.scan-input:disabled {
  background: #f1f5f9;
  cursor: not-allowed;
  color: #94a3b8;
}

.scan-field__pending {
  margin-top: 0.5rem;
  font-size: 0.78rem;
  color: #0e5b46;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
  background: rgba(22, 160, 122, 0.10);
  padding: 0.3rem 0.55rem;
  border-radius: 999px;
}

.scan-field__pending strong {
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-weight: 700;
  color: #0e5b46;
}

.scan-field__pending-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
  animation: pulse 1.4s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.scan-field__cancel {
  border: 0;
  background: transparent;
  color: #b45309;
  font-weight: 600;
  font-size: 0.75rem;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
}

.scan-field__hint-text {
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: #94a3b8;
}

.scan-zone__error {
  margin-top: 0.85rem;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  background: #fff1f2;
  color: #b91c1c;
  border: 1px solid #fecdd3;
  padding: 0.45rem 0.7rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
}

/* Status bar */
.status-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.1rem 0.25rem;
}

.status-bar__group {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.32rem 0.7rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 999px;
  font-size: 0.78rem;
  color: #475569;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.status-pill strong {
  color: #0e5b46;
  font-weight: 700;
}

.status-pill__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #16a07a;
}

.status-pill--danger {
  border-color: #fecdd3;
  background: #fff1f2;
  color: #b91c1c;
}

.status-pill--danger strong {
  color: #b91c1c;
}

/* Rows table card */
.rows-card {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  overflow: hidden;
}

.rows-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
}

.rows-card__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.rows-card__count {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #475569;
  background: #f1f5f9;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
}

.rows-card__empty {
  padding: 2.2rem 1rem;
  text-align: center;
  color: #64748b;
}

.empty-glyph {
  font-size: 2rem;
  color: #cbd5e1;
  margin-bottom: 0.4rem;
}

.rows-card__empty p {
  margin: 0;
  font-size: 0.88rem;
}

.rows-card__scroll {
  overflow-x: auto;
  max-height: 60vh;
}

.rows-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
}

.rows-table thead th {
  background: #f8fafc;
  color: #475569;
  font-weight: 700;
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 0.65rem 1rem;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 1;
  white-space: nowrap;
}

.rows-table tbody td {
  padding: 0.65rem 1rem;
  border-bottom: 1px solid #f1f5f9;
  color: #1f2937;
  vertical-align: middle;
}

.rows-table tbody tr:last-child td {
  border-bottom: none;
}

.rows-table tbody tr.is-failed td {
  background: #fff5f5;
}

.rows-table tbody tr.is-failed td:first-child {
  box-shadow: inset 3px 0 0 #dc2626;
}

.col-num {
  width: 56px;
  font-variant-numeric: tabular-nums;
  color: #64748b;
}

.cell-mono {
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-weight: 600;
  color: #1f2a44;
}

.cell-time {
  color: #64748b;
  font-variant-numeric: tabular-nums;
}

.cell-muted {
  color: #94a3b8;
}

.ship-tag {
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.6rem;
  background: #f5fbf8;
  border: 1px solid #c8ead9;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 600;
  color: #0e5b46;
  letter-spacing: 0.01em;
  white-space: nowrap;
}

.ship-tag--inline {
  background: #ffffff;
  border-color: #16a07a;
}

.scan-field__lookup-hint {
  font-size: 0.72rem;
  color: #6b7280;
  font-style: italic;
}

.scan-field__pending-buyer {
  font-size: 0.74rem;
  font-weight: 600;
  color: #1f2a44;
  background: #ffffff;
  border: 1px solid #d8dde6;
  padding: 0.16rem 0.55rem;
  border-radius: 999px;
  white-space: nowrap;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cell-buyer {
  font-size: 0.74rem;
  font-weight: 500;
  color: #475569;
  letter-spacing: 0;
}

.row-edit-input {
  width: 100%;
  height: 32px;
  padding: 0 0.55rem;
  border: 1px solid #16a07a;
  border-radius: 6px;
  background: #ffffff;
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 0.85rem;
  font-weight: 600;
  color: #1f2a44;
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.15);
}

.row-edit-input:focus {
  border-color: #0e5b46;
}

.row-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.row-edit-btn {
  height: 28px;
  padding: 0 0.7rem;
  border: 1px solid #c8ead9;
  border-radius: 6px;
  background: #f5fbf8;
  color: #0e5b46;
  font-size: 0.74rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.row-edit-btn:hover:not(:disabled) {
  background: #16a07a;
  border-color: #16a07a;
  color: #ffffff;
}

.row-edit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.row-save-btn {
  height: 28px;
  padding: 0 0.8rem;
  border: 0;
  border-radius: 6px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  font-size: 0.74rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(14, 91, 70, 0.18);
  transition: filter 0.15s ease;
}

.row-save-btn:hover:not(:disabled) {
  filter: brightness(1.05);
}

.row-save-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.row-ghost-btn {
  height: 28px;
  padding: 0 0.7rem;
  border: 1px solid #e5e9f2;
  border-radius: 6px;
  background: #ffffff;
  color: #475569;
  font-size: 0.74rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.row-ghost-btn:hover:not(:disabled) {
  border-color: #94a3b8;
  color: #1f2a44;
}

.rows-table tbody tr.is-editing td {
  background: #f8fafc;
}

.row-edit-error {
  font-size: 0.72rem;
  font-weight: 600;
  color: #b91c1c;
  margin-top: 0.25rem;
  display: block;
}

.cell-order {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.row-error {
  font-size: 0.72rem;
  font-weight: 600;
  color: #b91c1c;
}

.row-remove-btn {
  height: 28px;
  padding: 0 0.7rem;
  border: 1px solid #fecdd3;
  border-radius: 6px;
  background: #fff5f5;
  color: #b91c1c;
  font-size: 0.74rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.row-remove-btn:hover:not(:disabled) {
  background: #dc2626;
  border-color: #dc2626;
  color: #ffffff;
}

.row-remove-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.text-right { text-align: right; }

/* Confirm dialog */
.confirm-overlay {
  position: fixed;
  inset: 0;
  z-index: 1090;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  animation: fade-in 0.15s ease-out;
}

@keyframes fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

.confirm-dialog {
  background: #ffffff;
  border-radius: 12px;
  padding: 1.25rem 1.25rem 1rem;
  max-width: 420px;
  width: 100%;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.25);
}

.confirm-dialog h3 {
  margin: 0 0 0.4rem;
  font-size: 1rem;
  font-weight: 700;
  color: #1f2a44;
}

.confirm-dialog p {
  margin: 0 0 1rem;
  font-size: 0.85rem;
  color: #475569;
}

.confirm-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

/* Submit result dialog */
.result-dialog {
  background: #ffffff;
  border-radius: 14px;
  padding: 1.5rem 1.4rem 1.1rem;
  max-width: 460px;
  width: 100%;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.25);
  text-align: center;
  animation: result-pop 0.25s ease-out;
}

@keyframes result-pop {
  from { opacity: 0; transform: translateY(8px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

.result-dialog__icon {
  width: 60px;
  height: 60px;
  margin: 0 auto 0.85rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.result-dialog__icon.is-success {
  background: #f5fbf8;
  color: #0e5b46;
  box-shadow: 0 0 0 6px rgba(22, 160, 122, 0.12);
}

.result-dialog__icon.is-error {
  background: #fff1f2;
  color: #b91c1c;
  box-shadow: 0 0 0 6px rgba(220, 38, 38, 0.10);
}

.result-dialog__title {
  margin: 0 0 0.3rem;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
}

.result-dialog__subtitle {
  margin: 0 0 1rem;
  font-size: 0.88rem;
  color: #475569;
}

.result-dialog__subtitle strong {
  color: #0e5b46;
  font-weight: 700;
}

.result-fail {
  text-align: left;
  border: 1px solid #fecdd3;
  background: #fff7f7;
  border-radius: 10px;
  padding: 0.7rem 0.8rem;
  margin-bottom: 1rem;
}

.result-fail__head {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  font-weight: 700;
  color: #b91c1c;
  margin-bottom: 0.5rem;
}

.result-fail__list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 200px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.result-fail__item {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding: 0.45rem 0.55rem;
  background: #ffffff;
  border: 1px solid #fde0e3;
  border-radius: 8px;
}

.result-fail__order {
  font-size: 0.82rem;
}

.result-fail__reason {
  font-size: 0.74rem;
  font-weight: 600;
  color: #b91c1c;
}

.confetti-canvas {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1100;
}

/* Mobile */
@media (max-width: 720px) {
  .scanner-toolbar {
    align-items: stretch;
  }

  .scanner-toolbar__actions {
    width: 100%;
  }

  .scanner-toolbar__actions .ghost-btn,
  .scanner-toolbar__actions .submit-btn {
    flex: 1;
    justify-content: center;
  }

  .scan-zone__grid {
    grid-template-columns: 1fr;
  }

  .scan-input {
    height: 48px;
    font-size: 1.05rem;
  }

  .rows-table thead th,
  .rows-table tbody td {
    padding: 0.55rem 0.7rem;
  }

  .rows-card__scroll {
    max-height: 50vh;
  }
}
</style>
